import { Monitor, Cpu, MemoryStick, HardDrive, Camera, Battery, Wifi, Weight } from "lucide-react";

interface Props {
  specs?: {
    screen: string;
    processor: string;
    ram: string;
    storage: string;
    camera: string;
    frontCamera: string;
    battery: string;
    os: string;
    connectivity: string;
    weight: string;
  };
  ratings?: {
    screen: number;
    performance: number;
    camera: number;
    battery: number;
    value: number;
    overall: number;
  };
}

const ratingColor = (val: number) => {
  if (val >= 8) return "bg-green-500";
  if (val >= 6) return "bg-primary";
  return "bg-destructive";
};

const ratingLabels: Record<string, string> = {
  screen: "Tela",
  performance: "Desempenho",
  camera: "Câmera",
  battery: "Bateria",
  value: "Custo-benefício",
  overall: "Geral",
};

const specItems = [
  { key: "screen", label: "Tela", icon: Monitor },
  { key: "processor", label: "Processador", icon: Cpu },
  { key: "ram", label: "RAM", icon: MemoryStick },
  { key: "storage", label: "Armazenamento", icon: HardDrive },
  { key: "camera", label: "Câmera traseira", icon: Camera },
  { key: "frontCamera", label: "Câmera frontal", icon: Camera },
  { key: "battery", label: "Bateria", icon: Battery },
  { key: "connectivity", label: "Conectividade", icon: Wifi },
  { key: "weight", label: "Peso", icon: Weight },
] as const;

const PhoneSpecsPreview = ({ specs, ratings }: Props) => {
  return (
    <div className="space-y-4 animate-in fade-in duration-300">
      {/* Ratings */}
      {ratings && (
        <div className="space-y-2">
          <h5 className="text-sm font-bold text-foreground">Avaliações</h5>
          <div className="grid gap-2">
            {Object.entries(ratings).map(([key, val]) => (
              <div key={key} className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground w-28 shrink-0">
                  {ratingLabels[key] || key}
                </span>
                <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${ratingColor(val)}`}
                    style={{ width: `${(val / 10) * 100}%` }}
                  />
                </div>
                <span className="text-xs font-bold text-foreground w-8 text-right">
                  {val.toFixed(1)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Specs */}
      {specs && (
        <div className="space-y-2">
          <h5 className="text-sm font-bold text-foreground">Especificações</h5>
          <div className="grid gap-1.5">
            {specItems.map(({ key, label, icon: Icon }) => {
              const value = specs[key as keyof typeof specs];
              if (!value) return null;
              return (
                <div key={key} className="flex items-start gap-2 text-xs">
                  <Icon className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                  <span className="text-muted-foreground font-medium w-28 shrink-0">{label}</span>
                  <span className="text-foreground">{value}</span>
                </div>
              );
            })}
            {specs.os && (
              <div className="flex items-start gap-2 text-xs">
                <Cpu className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                <span className="text-muted-foreground font-medium w-28 shrink-0">Sistema</span>
                <span className="text-foreground">{specs.os}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhoneSpecsPreview;
